import type { Floor } from '@/world/builder';
import { idx } from '@/world/builder';
import { T_FLOOR, T_DOOR, T_STAIRS } from '@/world/types';

/** A* over the tile grid. `passDoor` decides whether a door tile is traversable. */
export function findPath(
  f: Floor, sx: number, sy: number, tx: number, ty: number,
  passDoor: (x: number, y: number) => boolean,
  maxNodes = 6000,
): Array<[number, number]> | null {
  const w = f.w, h = f.h;
  const walk = (x: number, y: number) => {
    if (x < 0 || y < 0 || x >= w || y >= h) return false;
    const i = idx(f, x, y);
    const t = f.tiles[i];
    if (t === T_DOOR) return passDoor(x, y);
    if (t !== T_FLOOR && t !== T_STAIRS) return false;
    return !f.solid[i];
  };
  if (!walk(tx, ty)) {
    // snap target to nearest walkable tile
    let best: [number, number] | null = null, bd = 1e9;
    for (let dy = -2; dy <= 2; dy++)
      for (let dx = -2; dx <= 2; dx++) {
        if (walk(tx + dx, ty + dy)) {
          const d = dx * dx + dy * dy;
          if (d < bd) { bd = d; best = [tx + dx, ty + dy]; }
        }
      }
    if (!best) return null;
    [tx, ty] = best;
  }
  const start = idx(f, sx, sy), goal = idx(f, tx, ty);
  if (start === goal) return [[tx, ty]];
  const g = new Map<number, number>();
  const came = new Map<number, number>();
  const open: number[] = [start];
  const fScore = new Map<number, number>();
  g.set(start, 0);
  const hfn = (i: number) => {
    const x = i % w, y = Math.floor(i / w);
    const dx = Math.abs(x - tx), dy = Math.abs(y - ty);
    return Math.max(dx, dy) + 0.41 * Math.min(dx, dy);
  };
  fScore.set(start, hfn(start));
  const closed = new Set<number>();
  let expanded = 0;
  while (open.length) {
    // pick lowest f (small open sets; linear scan is fine)
    let bi = 0, bf = fScore.get(open[0])!;
    for (let k = 1; k < open.length; k++) {
      const fs = fScore.get(open[k])!;
      if (fs < bf) { bf = fs; bi = k; }
    }
    const cur = open[bi];
    open[bi] = open[open.length - 1];
    open.pop();
    if (cur === goal) {
      const path: Array<[number, number]> = [];
      let c = cur;
      while (c !== start) {
        path.push([c % w, Math.floor(c / w)]);
        c = came.get(c)!;
      }
      path.reverse();
      return path;
    }
    closed.add(cur);
    if (++expanded > maxNodes) return null;
    const cx = cur % w, cy = Math.floor(cur / w);
    const gc = g.get(cur)!;
    for (let dy = -1; dy <= 1; dy++)
      for (let dx = -1; dx <= 1; dx++) {
        if (!dx && !dy) continue;
        const nx = cx + dx, ny = cy + dy;
        if (!walk(nx, ny)) continue;
        if (dx && dy) {
          // no corner cutting
          if (!walk(cx + dx, cy) || !walk(cx, cy + dy)) continue;
          // doors only orthogonally
          if (f.tiles[idx(f, nx, ny)] === T_DOOR || f.tiles[cur] === T_DOOR) continue;
        }
        const ni = idx(f, nx, ny);
        if (closed.has(ni)) continue;
        const ng = gc + (dx && dy ? 1.41 : 1);
        if (ng < (g.get(ni) ?? Infinity)) {
          g.set(ni, ng);
          came.set(ni, cur);
          fScore.set(ni, ng + hfn(ni));
          if (!open.includes(ni)) open.push(ni);
        }
      }
  }
  return null;
}

/** BFS distance field from a tile, for "nearest X" queries. */
export function nearestTile(
  f: Floor, sx: number, sy: number, pred: (x: number, y: number) => boolean,
  passDoor: (x: number, y: number) => boolean, limit = 4000,
): [number, number] | null {
  const w = f.w, h = f.h;
  const seen = new Uint8Array(w * h);
  const q: number[] = [idx(f, sx, sy)];
  seen[q[0]] = 1;
  let n = 0;
  while (q.length && n++ < limit) {
    const i = q.shift()!;
    const x = i % w, y = Math.floor(i / w);
    if (pred(x, y)) return [x, y];
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const nx = x + dx, ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
      const ni = idx(f, nx, ny);
      if (seen[ni]) continue;
      const t = f.tiles[ni];
      const ok = t === T_DOOR ? passDoor(nx, ny) : (t === T_FLOOR || t === T_STAIRS) && !f.solid[ni];
      if (!ok) continue;
      seen[ni] = 1;
      q.push(ni);
    }
  }
  return null;
}
